import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore/lite";
import { db } from "../services/firebase";
import { LegalPageLayout } from "./LegalPageLayout";
import {
  LEGAL_COLLECTION,
  LEGAL_DOCUMENT_TITLES,
  toLegalDocumentContent,
  type LegalDocumentContent,
  type LegalDocumentId,
} from "../legal/shared/documents";
import { parseLegalMarkdown, type LegalBlock, type LegalInline } from "../legal/shared/markdown";

const SUBTITLE = "Dough Mobile Application";
const HELP_EMAIL = "help@betteryou.ai";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; content: LegalDocumentContent }
  | { status: "error" };

/** A `\n` inside inline text is a hard line break. */
function withLineBreaks(text: string): ReactNode {
  const lines = text.split("\n");
  if (lines.length === 1) return text;
  return lines.map((line, i) => (
    <Fragment key={i}>
      {i > 0 ? <br /> : null}
      {line}
    </Fragment>
  ));
}

function renderLink(href: string, children: ReactNode, key: number): ReactNode {
  if (href.startsWith("/")) {
    return (
      <Link key={key} to={href}>
        {children}
      </Link>
    );
  }
  if (/^mailto:/i.test(href)) {
    return (
      <a key={key} href={href}>
        {children}
      </a>
    );
  }
  return (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

/**
 * `ignoreBold` is for notices: the whole paragraph is one bold span, and
 * `p.uppercase-notice` already sets its weight.
 */
function renderInlines(inlines: LegalInline[], ignoreBold = false): ReactNode[] {
  return inlines.map((inline, i) => {
    let node: ReactNode = withLineBreaks(inline.text);
    if (inline.bold && !ignoreBold) node = <strong>{node}</strong>;
    if (inline.href) return renderLink(inline.href, node, i);
    return <Fragment key={i}>{node}</Fragment>;
  });
}

function renderBlock(block: LegalBlock, key: number): ReactNode {
  switch (block.type) {
    case "heading":
      return block.level === 2 ? <h2 key={key}>{block.text}</h2> : <h3 key={key}>{block.text}</h3>;
    case "paragraph":
      return block.notice ? (
        <p key={key} className="uppercase-notice">
          {renderInlines(block.inlines, true)}
        </p>
      ) : (
        <p key={key}>{renderInlines(block.inlines)}</p>
      );
    case "list":
      return (
        <ul key={key}>
          {block.items.map((item, i) => (
            <li key={i}>{renderInlines(item)}</li>
          ))}
        </ul>
      );
  }
}

/**
 * Each `##` heading and the blocks after it share one `<section>`, as the
 * hand-written pages did: `.legal-content` spaces its children with
 * `space-y-8`, so the gap belongs between sections, not between paragraphs.
 */
function groupIntoSections(blocks: LegalBlock[]): LegalBlock[][] {
  const sections: LegalBlock[][] = [];
  for (const block of blocks) {
    const startsSection = block.type === "heading" && block.level === 2;
    if (startsSection || sections.length === 0) sections.push([]);
    sections[sections.length - 1].push(block);
  }
  return sections;
}

interface LegalDocumentPageProps {
  documentId: LegalDocumentId;
}

/**
 * The hosted Terms of Service or Privacy Policy (FNDTN-33). The text lives in
 * Firestore at `legal_documents/{documentId}`, which anyone may read, and is
 * edited from the admin web app; this page reads it once per visit.
 */
export function LegalDocumentPage({ documentId }: LegalDocumentPageProps) {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    getDoc(doc(db, LEGAL_COLLECTION, documentId))
      .then((snapshot) => {
        if (cancelled) return;
        const content = snapshot.exists() ? toLegalDocumentContent(snapshot.data()) : null;
        setState(content ? { status: "ready", content } : { status: "error" });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const fallbackTitle = LEGAL_DOCUMENT_TITLES[documentId];

  if (state.status === "ready") {
    const { title, lastUpdated, body } = state.content;
    return (
      <LegalPageLayout title={title} subtitle={SUBTITLE} lastUpdated={lastUpdated}>
        {groupIntoSections(parseLegalMarkdown(body)).map((section, i) => (
          <section key={i}>{section.map(renderBlock)}</section>
        ))}
      </LegalPageLayout>
    );
  }

  return (
    <LegalPageLayout title={fallbackTitle} subtitle={SUBTITLE}>
      {state.status === "loading" ? (
        <p aria-live="polite">Loading…</p>
      ) : (
        <p role="alert">
          We couldn't load the {fallbackTitle} right now. Please refresh the page, or email{" "}
          <a href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a>.
        </p>
      )}
    </LegalPageLayout>
  );
}
